import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Truck, MapPin, Phone, Clock, ChevronLeft, ChevronRight, CheckCircle, Navigation, StickyNote, Camera } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const shiftDate = (iso, days) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const prettyDate = (iso) =>
  new Date(iso + 'T00:00:00').toLocaleDateString('en-SG', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });

const mapsUrl = (addr) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`;

const JobCard = ({ job, driverName, onDelivered }) => {
  const [marking, setMarking] = useState(false);
  const [photoMode, setPhotoMode] = useState(false);
  const [photos, setPhotos] = useState(job.proof_photos || []);
  const [uploading, setUploading] = useState(false);

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('driver_name', driverName);
        fd.append('file', f);
        const res = await axios.post(`${API}/api/public/driver/jobs/${job.id}/photos`, fd);
        setPhotos((prev) => [...prev, res.data.photo]);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Photo upload failed, please try again');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const markDelivered = async () => {
    if (photos.length === 0) {
      setPhotoMode(true);
      return;
    }
    if (!window.confirm(`Mark job ${job.document_number} as delivered?`)) return;
    setMarking(true);
    try {
      await axios.post(`${API}/api/public/driver/jobs/${job.id}/delivered`, { driver_name: driverName });
      onDelivered();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update job');
    } finally {
      setMarking(false);
    }
  };

  return (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4" data-testid={`driver-job-${job.document_number}`}>
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div className="flex items-center gap-2">
        <span className="font-bold text-[#5c1a1a]">{job.document_number}</span>
        {job.delivered ? (
          <span className="text-xs font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">✓ Delivered</span>
        ) : (
          <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Pending</span>
        )}
      </div>
      {job.delivery_time && (
        <span className="flex items-center gap-1 text-sm font-semibold text-gray-700">
          <Clock size={14} className="text-[#5c1a1a]" /> {job.delivery_time}
        </span>
      )}
    </div>
    <p className="font-semibold text-gray-900 mt-2">{job.customer_name}</p>
    {job.vehicle_number && <p className="text-xs text-gray-500">Vehicle: {job.vehicle_number}</p>}

    <div className="mt-3 space-y-2 text-sm">
      {job.pickup_address && (
        <div className="flex items-start gap-2">
          <MapPin size={16} className="text-gray-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-xs text-gray-400 uppercase">Pickup</p>
            <p className="text-gray-700">{job.pickup_address}</p>
          </div>
        </div>
      )}
      <div className="flex items-start gap-2">
        <MapPin size={16} className="text-[#5c1a1a] mt-0.5 shrink-0" />
        <div>
          <p className="text-xs text-gray-400 uppercase">Delivery</p>
          <p className="text-gray-700">{job.delivery_address}</p>
        </div>
      </div>
      {job.notes && (
        <div className="flex items-start gap-2">
          <StickyNote size={16} className="text-amber-500 mt-0.5 shrink-0" />
          <p className="text-gray-600 text-xs">{job.notes}</p>
        </div>
      )}
    </div>

    <div className="grid grid-cols-2 gap-2 mt-4">
      <a
        href={mapsUrl(job.delivery_address)}
        target="_blank"
        rel="noreferrer"
        className="flex items-center justify-center gap-1.5 bg-[#5c1a1a] text-white rounded-lg py-2.5 text-sm font-semibold active:scale-95 transition-transform"
        data-testid={`navigate-${job.document_number}`}
      >
        <Navigation size={16} /> Navigate
      </a>
      {(job.site_contact_phone || job.customer_phone) ? (
        <a
          href={`tel:${job.site_contact_phone || job.customer_phone}`}
          className="flex items-center justify-center gap-1.5 bg-green-600 text-white rounded-lg py-2.5 text-sm font-semibold active:scale-95 transition-transform"
          data-testid={`call-${job.document_number}`}
        >
          <Phone size={16} /> Call {job.site_contact_name ? job.site_contact_name.split(' ')[0] : 'Customer'}
        </a>
      ) : (
        <div className="flex items-center justify-center bg-gray-100 text-gray-400 rounded-lg py-2.5 text-sm">No contact</div>
      )}
      {job.pickup_address && (
        <a
          href={mapsUrl(job.pickup_address)}
          target="_blank"
          rel="noreferrer"
          className="flex items-center justify-center gap-1.5 border border-[#5c1a1a] text-[#5c1a1a] rounded-lg py-2.5 text-sm font-semibold col-span-2 active:scale-95 transition-transform"
          data-testid={`navigate-pickup-${job.document_number}`}
        >
          <Navigation size={16} /> Navigate to Pickup
        </a>
      )}
      {!job.delivered && (
        <div className="col-span-2 space-y-2">
          {photoMode && (
            <div className="border border-dashed border-gray-300 rounded-lg p-3 bg-gray-50" data-testid={`photo-panel-${job.document_number}`}>
              <p className="text-xs font-semibold text-gray-700 mb-2">📷 Photo of delivered goods (required)</p>
              {photos.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {photos.map((p) => (
                    <img
                      key={p.id}
                      src={`${API}/api/public/driver/photos/${job.id}/${p.id}`}
                      alt="proof"
                      className="w-16 h-16 object-cover rounded border"
                      data-testid={`proof-thumb-${p.id}`}
                    />
                  ))}
                </div>
              )}
              <label className="flex items-center justify-center gap-1.5 border border-[#5c1a1a] text-[#5c1a1a] rounded-lg py-2.5 text-sm font-semibold cursor-pointer bg-white">
                <Camera size={16} /> {uploading ? 'Uploading...' : photos.length ? 'Add Another Photo' : 'Take / Upload Photo'}
                <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={uploadFiles} disabled={uploading} data-testid={`photo-input-${job.document_number}`} />
              </label>
            </div>
          )}
          <button
            onClick={markDelivered}
            disabled={marking || uploading || (photoMode && photos.length === 0)}
            className="flex items-center justify-center gap-1.5 bg-emerald-600 text-white rounded-lg py-3 text-sm font-bold w-full active:scale-95 transition-transform disabled:opacity-60"
            data-testid={`mark-delivered-${job.document_number}`}
          >
            <CheckCircle size={18} /> {marking ? 'Updating...' : photoMode && photos.length === 0 ? 'Add a Photo First' : 'Mark Delivered'}
          </button>
        </div>
      )}
    </div>
  </div>
  );
};

export default function DriverJobs() {
  const { name } = useParams();
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchJobs = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/public/driver/${encodeURIComponent(name)}/jobs`, { params: { date: d } });
      setData(res.data);
    } catch {
      setData({ driver_name: name, date: d, jobs: [] });
    } finally {
      setLoading(false);
    }
  }, [name]);

  useEffect(() => { fetchJobs(date); }, [date, fetchJobs]);

  return (
    <div className="min-h-screen bg-gray-100" data-testid="driver-jobs-page">
      <div className="bg-[#5c1a1a] text-white px-4 pt-6 pb-5 sticky top-0 z-10 shadow-md">
        <div className="max-w-md mx-auto">
          <div className="flex items-center gap-2">
            <Truck size={22} className="text-amber-300" />
            <h1 className="text-lg font-bold">NJK Driver Job Sheet</h1>
          </div>
          <p className="text-white/80 text-sm mt-0.5" data-testid="driver-name">{data?.driver_name || name}</p>
          <div className="flex items-center justify-between mt-3 bg-white/10 rounded-lg px-2 py-1.5">
            <button onClick={() => setDate(shiftDate(date, -1))} className="p-1.5 rounded hover:bg-white/10" data-testid="prev-day-btn">
              <ChevronLeft size={20} />
            </button>
            <button onClick={() => setDate(todayISO())} className="text-sm font-semibold" data-testid="date-label">
              {date === todayISO() ? 'Today — ' : ''}{prettyDate(date)}
            </button>
            <button onClick={() => setDate(shiftDate(date, 1))} className="p-1.5 rounded hover:bg-white/10" data-testid="next-day-btn">
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-4 space-y-4 pb-16">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5c1a1a]"></div>
          </div>
        ) : data?.jobs?.length ? (
          <>
            <p className="text-sm text-gray-500" data-testid="job-count">{data.jobs.length} job{data.jobs.length !== 1 ? 's' : ''} scheduled</p>
            {data.jobs.map((job) => <JobCard key={job.id} job={job} driverName={data.driver_name} onDelivered={() => fetchJobs(date)} />)}
          </>
        ) : (
          <div className="text-center py-16 text-gray-500" data-testid="no-jobs">
            <CheckCircle size={40} className="mx-auto text-green-500 mb-3" />
            No jobs scheduled for this day.
          </div>
        )}
        <p className="text-center text-xs text-gray-400 pt-4">NJK Logistics Pte Ltd · +65 9836 6186</p>
      </div>
    </div>
  );
}
